import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import type { AutoPromptEffort, CreateCsvTransformJobItem, DataLoadJobConfiguration, OnboardingJob, OnboardingJobStatus } from "../../services/backend.service";
import { backendService } from "../../services/backend.service";

export const ONBOARDING_JOBS_QUERY_KEY = "onboarding_jobs";
const ACTIVE_POLL_INTERVAL_MS = 2_000;

const ACTIVE_JOB_STATUSES: ReadonlySet<OnboardingJobStatus> = new Set(["queued", "running", "cancel_requested"]);

type JobObservation = { status: OnboardingJobStatus; eligibleForTerminalAnalytics: boolean };
type TerminalJobStatus = Extract<OnboardingJobStatus, "succeeded" | "partially_succeeded" | "failed" | "cancelled">;
const jobObservations = new Map<string, Map<string, JobObservation>>();

export const isOnboardingJobActive = (job: Pick<OnboardingJob, "status"> | undefined): boolean => Boolean(job && ACTIVE_JOB_STATUSES.has(job.status));

export const onboardingJobsQueryKey = (departmentId: string | undefined) => [ONBOARDING_JOBS_QUERY_KEY, departmentId] as const;
export const onboardingJobDetailQueryKey = (departmentId: string | undefined, jobId: string | undefined) =>
	[ONBOARDING_JOBS_QUERY_KEY, departmentId, "detail", jobId] as const;

export const onboardingJobsPollingInterval = (jobs: OnboardingJob[] | undefined): number | false =>
	jobs?.some(isOnboardingJobActive) ? ACTIVE_POLL_INTERVAL_MS : false;

export const onboardingJobPollingInterval = (job: OnboardingJob | undefined): number | false => (isOnboardingJobActive(job) ? ACTIVE_POLL_INTERVAL_MS : false);

const isTerminalStatus = (status: OnboardingJobStatus): status is TerminalJobStatus => !ACTIVE_JOB_STATUSES.has(status);

export const recordCreatedOnboardingJob = (job: OnboardingJob): void => {
	let department = jobObservations.get(job.department_id);
	if (!department) {
		department = new Map<string, JobObservation>();
		jobObservations.set(job.department_id, department);
	}
	department.set(job.id, { status: job.status, eligibleForTerminalAnalytics: true });
};

export const observeOnboardingJobTransitions = (departmentId: string, jobs: OnboardingJob[]): Array<{ job: OnboardingJob; status: TerminalJobStatus }> => {
	const existingDepartment = jobObservations.get(departmentId);
	if (!existingDepartment) {
		jobObservations.set(departmentId, new Map(jobs.map((job) => [job.id, { status: job.status, eligibleForTerminalAnalytics: isOnboardingJobActive(job) }])));
		return [];
	}

	const terminalTransitions: Array<{ job: OnboardingJob; status: TerminalJobStatus }> = [];
	for (const job of jobs) {
		const previous = existingDepartment.get(job.id);
		if (!previous) {
			existingDepartment.set(job.id, { status: job.status, eligibleForTerminalAnalytics: isOnboardingJobActive(job) });
			continue;
		}
		if (previous.status === job.status) continue;
		const eligibleForTerminalAnalytics = previous.eligibleForTerminalAnalytics || isOnboardingJobActive(job);
		existingDepartment.set(job.id, { status: job.status, eligibleForTerminalAnalytics });
		if (eligibleForTerminalAnalytics && isTerminalStatus(job.status)) terminalTransitions.push({ job, status: job.status });
	}
	return terminalTransitions;
};

export const resetOnboardingJobObservationsForTests = (): void => jobObservations.clear();

const errorDetail = (error: unknown): string | undefined => {
	// Axios response errors are the only network error shape this wrapper exposes to callers.
	const axiosError = error as AxiosError<{ detail?: string | { message?: string } }>;
	const detail = axiosError.response?.data?.detail;
	return typeof detail === "string" ? detail : detail?.message;
};

export const isOnboardingArtifactUnavailableError = (error: unknown): boolean => {
	// Axios is the only network transport used by onboarding services.
	const axiosError = error as AxiosError<{ detail?: string }>;
	return axiosError.response?.status === 410 || axiosError.response?.data?.detail === "onboarding_jobs.artifact_unavailable";
};

export const isOnboardingJobNotFoundError = (error: unknown): boolean => {
	// Axios is the only network transport used by onboarding services.
	const axiosError = error as AxiosError<{ detail?: string }>;
	return axiosError.response?.status === 404 || axiosError.response?.data?.detail === "onboarding_jobs.not_found";
};

const invalidateJobQueries = (queryClient: ReturnType<typeof useQueryClient>, departmentId: string, jobId?: string) => {
	void queryClient.invalidateQueries({ queryKey: onboardingJobsQueryKey(departmentId) });
	if (jobId) void queryClient.invalidateQueries({ queryKey: onboardingJobDetailQueryKey(departmentId, jobId) });
};

export function useOnboardingJobs(departmentId: string | undefined) {
	const { track, trackError } = useAnalytics();
	const query = useQuery({
		queryKey: onboardingJobsQueryKey(departmentId),
		queryFn: async (): Promise<OnboardingJob[]> => {
			if (!departmentId) throw new Error("department_id is required");
			const response = await backendService.listOnboardingJobs({ departmentId });
			return response.data.jobs;
		},
		enabled: Boolean(departmentId),
		refetchInterval: (state) => onboardingJobsPollingInterval(state.state.data),
		refetchIntervalInBackground: false,
		refetchOnWindowFocus: true,
		refetchOnReconnect: true,
	});

	useEffect(() => {
		if (!query.data) return;
		if (!departmentId) return;
		for (const { job, status } of observeOnboardingJobTransitions(departmentId, query.data)) {
			const properties = {
				department_id: job.department_id,
				job_id: job.id,
				job_type: job.job_type,
				status: job.status,
				completed_items: job.completed_items,
				total_items: job.total_items,
			};
			if (status === "succeeded") track("onboarding_run_succeeded", properties);
			else if (status === "partially_succeeded") track("onboarding_run_partially_succeeded", properties);
			else if (status === "cancelled") track("onboarding_run_cancelled", properties);
			else trackError("onboarding_run_failed", properties);
		}
	}, [departmentId, query.data, track, trackError]);

	return query;
}

export function useOnboardingJob(jobId: string | undefined, departmentId: string | undefined) {
	return useQuery({
		queryKey: onboardingJobDetailQueryKey(departmentId, jobId),
		queryFn: async (): Promise<OnboardingJob> => {
			if (!jobId || !departmentId) throw new Error("job_id and department_id are required");
			const response = await backendService.getOnboardingJob(jobId, departmentId);
			return response.data;
		},
		enabled: Boolean(jobId && departmentId),
		refetchInterval: (state) => onboardingJobPollingInterval(state.state.data),
		refetchIntervalInBackground: false,
		refetchOnWindowFocus: true,
		refetchOnReconnect: true,
	});
}

export function useCreateAutoPromptJob() {
	const { t } = useTranslation();
	const { showErrorSnackbar } = useHospitalData();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (params: {
			departmentId: string;
			effort: AutoPromptEffort;
			idempotencyKey: string;
			rosterFiles: File[];
			shiftsFiles: File[];
			templateFiles: File[];
		}): Promise<OnboardingJob> => {
			const response = await backendService.createAutoPromptJob(params);
			return response.data;
		},
		onSuccess: (job) => {
			recordCreatedOnboardingJob(job);
			invalidateJobQueries(queryClient, job.department_id, job.id);
		},
		onError: (error, params) => {
			// DEFAULT: unexpected response shapes still need an actionable message rather than a blank snackbar.
			showErrorSnackbar(errorDetail(error) ?? t("onboarding_jobs.errors.create_analysis"), { department_id: params.departmentId }, error);
		},
	});
}

export function useCreateCsvTransformJob() {
	const { t } = useTranslation();
	const { showErrorSnackbar } = useHospitalData();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (
			params: {
				departmentId: string;
				idempotencyKey: string;
				items: CreateCsvTransformJobItem[];
			} & (
				| { origin: "data_load"; files: File[]; sourceJobId?: never; configuration: DataLoadJobConfiguration }
				| { origin: "auto_prompt"; files?: never; sourceJobId: string; configuration?: never }
			),
		): Promise<OnboardingJob> => {
			const response = await backendService.createCsvTransformJob(params);
			return response.data;
		},
		onSuccess: (job) => {
			recordCreatedOnboardingJob(job);
			invalidateJobQueries(queryClient, job.department_id, job.id);
		},
		onError: (error, params) => {
			// DEFAULT: unexpected response shapes still need an actionable message rather than a blank snackbar.
			showErrorSnackbar(errorDetail(error) ?? t("onboarding_jobs.errors.create_transform"), { department_id: params.departmentId }, error);
		},
	});
}

export function useCancelOnboardingJob() {
	const { t } = useTranslation();
	const { showErrorSnackbar } = useHospitalData();
	const queryClient = useQueryClient();
	const { track, trackError } = useAnalytics();

	return useMutation({
		mutationFn: async (params: { departmentId: string; jobId: string }): Promise<OnboardingJob> => {
			const response = await backendService.cancelOnboardingJob(params.jobId, params.departmentId);
			return response.data;
		},
		onMutate: (params) => track("onboarding_run_cancel_start", { department_id: params.departmentId, job_id: params.jobId }),
		onSuccess: (job) => {
			track("onboarding_run_cancel_success", { department_id: job.department_id, job_id: job.id, status: job.status });
			invalidateJobQueries(queryClient, job.department_id, job.id);
		},
		onError: (error, params) => {
			trackError("onboarding_run_cancel_error", { department_id: params.departmentId, job_id: params.jobId, error_message: errorDetail(error) });
			showErrorSnackbar(t("onboarding_jobs.errors.cancel"), { department_id: params.departmentId, job_id: params.jobId }, error);
		},
	});
}

export function useRetryOnboardingJobItem() {
	const { t } = useTranslation();
	const { showErrorSnackbar } = useHospitalData();
	const queryClient = useQueryClient();
	const { track, trackError } = useAnalytics();

	return useMutation({
		mutationFn: async (params: { departmentId: string; jobId: string; itemId: string }): Promise<OnboardingJob> => {
			const response = await backendService.retryOnboardingJobItem(params.jobId, params.itemId, params.departmentId);
			return response.data;
		},
		onMutate: (params) => track("onboarding_file_retry_start", { department_id: params.departmentId, job_id: params.jobId, item_id: params.itemId }),
		onSuccess: (job, params) => {
			track("onboarding_file_retry_success", { department_id: job.department_id, job_id: job.id, item_id: params.itemId, status: job.status });
			invalidateJobQueries(queryClient, job.department_id, job.id);
		},
		onError: (error, params) => {
			trackError("onboarding_file_retry_error", {
				department_id: params.departmentId,
				job_id: params.jobId,
				item_id: params.itemId,
				error_message: errorDetail(error),
			});
			showErrorSnackbar(t("onboarding_jobs.errors.retry_file"), { department_id: params.departmentId, job_id: params.jobId }, error);
		},
	});
}
